import { computed, inject, Service, signal } from '@angular/core';
import { filter, firstValueFrom, merge, Observable } from 'rxjs';
import { WalletEventResponse, WalletTransferRequestResponse } from '../../../models/wallet-response.model';
import { PrivateMessageEventConsumerService } from '../../../events/private-message/private-message-event.consumer.service';
import { WalletService } from '../../../services/wallet.service';
import { TransferHistoryItem } from '../transfer-history/wallet-transfer-history.model';
import { applyTransferEvents, toTransferHistoryItem } from '../transfer-history/wallet-transfer-history.utils';
import { WalletViewStateService } from './wallet-view-state.service';

/**
 * The rows a transfer list shows and the one its detail sheet is open on — shared by
 * «تراکنش‌های اخیر» on the wallet tab and the full تاریخچه تراکنش‌ها page, which differ
 * only in the endpoint they read and how they lay the rows out.
 *
 * Provided per list, so each keeps rows of its own.
 */
@Service({ autoProvided: false })
export class WalletTransferRowsStore {
	private readonly walletService = inject(WalletService);
	private readonly viewState = inject(WalletViewStateService);
	private readonly privateMessages = inject(PrivateMessageEventConsumerService);

	private readonly rows = signal<TransferHistoryItem[]>([]);
	private readonly selectedId = signal<string | null>(null);
	/** The events last read per row, laid back over a re-read row until fresh ones arrive. */
	private readonly eventsById = new Map<string, WalletEventResponse[]>();

	/** Newest first. */
	readonly requests = this.rows.asReadonly();

	/**
	 * The row the sheet is open on, looked up in the list rather than held aside: the
	 * timeline redrawn from the request's events has to reach a sheet that is already open.
	 */
	readonly selectedRequest = computed(() => this.rows().find(request => request.id === this.selectedId()) ?? null);

	/**
	 * The server saying the money moved — a transfer's status has changed — so the list is
	 * stale. The account's own text is one of the ways it says so: a transfer landing
	 * arrives as a `Text_IME` message, not as a remain reload.
	 */
	readonly stale$: Observable<unknown> = merge(
		this.privateMessages.textReceived$.pipe(filter(message => message.kind === 'Text_IME')),
		this.privateMessages.remainReloadRequested$
	);

	/** Lays read records in, newest first; `append` keeps the pages already read. */
	setRows(transfers: readonly WalletTransferRequestResponse[], append: boolean): void {
		const items = transfers.map(request => this.toItem(request));

		this.rows.update(existing =>
			(append ? [...existing, ...items] : items).sort((first, second) =>
				second.createdDate.localeCompare(first.createdDate)
			)
		);
	}

	select(request: TransferHistoryItem): void {
		this.selectedId.set(request.id);
	}

	clearSelection(): void {
		this.selectedId.set(null);
	}

	/** Re-read rows carry only their state; the open sheet reads its events again. */
	reloadSelectedEvents(): Promise<void> {
		const selected = this.selectedRequest();

		return selected ? this.loadEvents(selected) : Promise.resolve();
	}

	/**
	 * Reads the events behind one request and redraws its timeline from them, so the sheet
	 * shows the stops the money has actually taken rather than the one state the record
	 * reports.
	 *
	 * The events hang off the number the endpoint gave the record, and a gateway payment is
	 * often still unnumbered when the history is read — the number it is given afterwards is
	 * looked for here, which is also what makes a sheet opened straight after a payment show
	 * the stops that payment has since taken. A record with no number to ask about, or one
	 * whose events cannot be read, keeps the timeline its state drew: neither is worth an
	 * error, and both are still a result the trader came to see.
	 */
	async loadEvents(request: TransferHistoryItem): Promise<void> {
		const apiId = request.apiId ?? (await this.rereadPaymentId(request));

		if (!apiId) {
			return;
		}

		try {
			const events = await firstValueFrom(this.walletService.getEvents(apiId));
			this.applyEvents(request.id, events);
		} catch {
			// The state-derived timeline remains available when events cannot be read.
		}
	}

	/** A request as a row, with the events last read for it laid back over its timeline. */
	private toItem(request: WalletTransferRequestResponse): TransferHistoryItem {
		const item = toTransferHistoryItem(request, this.viewState.defaultWalletKey());
		const events = this.eventsById.get(item.id);

		return events ? applyTransferEvents(item, events) : item;
	}

	/**
	 * Transfer requests already carry the aggregate id used by the events endpoint.
	 */
	private async rereadPaymentId(request: TransferHistoryItem): Promise<string | null> {
		return request.source === 'transferRequest' ? request.apiId : null;
	}

	private applyEvents(id: string, events: WalletEventResponse[]): void {
		if (!events.length) {
			return;
		}

		this.eventsById.set(id, events);
		this.rows.update(requests =>
			requests.map(request => (request.id === id ? applyTransferEvents(request, events) : request))
		);
	}
}
